import { MatchConfig } from "./assignment";
import Cacheable from "./cacheable";
import { SessionProfileQueryBinding, SessionProfileAttribute, Loader } from "./session";
import { QueryParser } from "./queryparser";
import { LogLevelEnum, SENTRY_DSN, Telemetry } from "./telemetry";
import Refresher from "./refresher";

declare global {
    interface Window {
        // add support for mocking window elements
        // to simulate source parsing
        __mock_window_search?: string;
        __mock_referrer?: string;
    }
    interface Navigator {
        deviceMemory?: number;
        hardwareConcurrency?: number;
        language?: number;
        userAgent?: string;
        vendor?: string;
    }
}


interface AnalyticsConfiguration {
    interval: number;
    maxRefreshes: number;
}


export default class Fountain
{

    protected configurationUrl: string;
    protected configuration : AnalyticsConfiguration | null;

    protected queries : Array<SessionProfileQueryBinding>;
    
    protected leadSourceConfigurationUrl: string;
    protected leadSourceConfiguration: MatchConfig | null;

    protected cache: Cacheable;
    protected telemetry: Telemetry;

    protected currentRefresh: number;
    protected maxRefreshes: number;

    protected loader: Loader;
    
    constructor(
        configuration: AnalyticsConfiguration | null = null,
        configurationUrl: string | null = null, 
        leadSourceConfigurationUrl: string | null = null,
        leadSourceConfiguration: MatchConfig | null = null,
    )
    {
        this.configurationUrl = configurationUrl;
        this.leadSourceConfigurationUrl = leadSourceConfigurationUrl;
        this.configuration = configuration;
        this.leadSourceConfiguration = leadSourceConfiguration;
        // store the queries here so that
        // when we refresh on interval we have the query-element
        // relationship bound
        this.queries = [];
        this.appendNewQueries();
        this.cache = new Cacheable();
        this.loader = new Loader();
        // reset "hitId" on each 'hit' of the website
        // (assumes this library is loaded once per hit)
        this.cache.store.set(SessionProfileAttribute.hitId, []);
        this.telemetry = new Telemetry(LogLevelEnum.ERROR, SENTRY_DSN);
        this.currentRefresh = 0;
        this.maxRefreshes = 3;
        setTimeout(this.main.bind(this), 0);
    }

    public async main()
    {
        try {
            this.doMain();
        } catch (error) {
            this.telemetry.log(
                LogLevelEnum.CRITICAL, 
                String(error), 
                {},
            );
        }
        this.telemetry.checkForTest();
    }

    protected async doMain()
    {
        if (this.configurationUrl) {
            this.configuration = await this.fetchJsonConfig(this.configurationUrl);
        }
        if (this.configuration === null) {
            throw 'No configuration or invalid configuration provided.';
        }

        if (this.leadSourceConfigurationUrl) {
            this.leadSourceConfiguration = await this.fetchJsonConfig(this.leadSourceConfigurationUrl);
        }
        if (this.leadSourceConfiguration === null) {
            throw 'No lead source configuration or invalid configuration provided';
        }
        // ensure normal query refreshment takes precedence
        this.refreshQueries();

        // then expose an event to allow one-off query refreshment
        // to allow signalling for unexpected DOM structure changes
        window.addEventListener(
            'Fountain.Query.Refresh',
            this.doSingleRefresh.bind(this),
        );
    }

    protected async fetchJsonConfig(url: string)
    {
        const response = await fetch(url);
        const json = await response.json();
        return json;   
    }

    protected appendNewQueries() : void
    {
        // find additional queries through hidden fields not immediately visible
        let additional = QueryParser.getAll();
        if (additional.length > 0) {
            this.queries = this.queries.concat(additional);
        }
    }

    public doSingleRefresh() : void
    {
        // look for additional hidden inputs to inject values into
        this.appendNewQueries();
        const sessionProfile = this.loader.execute();
        Refresher.execute(this.queries, this.leadSourceConfiguration, sessionProfile);
    }

    protected refreshQueries() : void
    {
        this.doSingleRefresh();
        let interval = 1000;
        if (this.configuration.interval) {
            interval = this.configuration.interval;
        }
        if (this.configuration.maxRefreshes) {
            this.maxRefreshes = this.configuration.maxRefreshes;
        }
        if (this.currentRefresh < this.maxRefreshes) {
            // set the next refresh
            setTimeout(
                this.refreshQueries.bind(this),
                interval,
            );
            this.currentRefresh ++;
        }
    }
}